import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../../context/API';
import { TrendingItemsData } from '../types';

// Request parameters
export interface TrendingItemsRequestParams {
  startDate: string;
  endDate: string;
  merchantId?: string;
  store?: string | null;
  categoryId?: string;
}

interface TrendingItemsState {
  data: TrendingItemsData | null;
  loading: boolean;
  error: string | null;
}

const initialState: TrendingItemsState = {
  data: null,
  loading: false,
  error: null,
};

// Async thunk to fetch trending items data
export const fetchTrendingItems = createAsyncThunk<
  TrendingItemsData,
  TrendingItemsRequestParams,
  { rejectValue: string }
>('trendingItems/fetchTrendingItems', async (params, { rejectWithValue }) => {
  try {
    const requestParams: Record<string, any> = {
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.merchantId) {
      requestParams.merchantId = params.merchantId;
    }

    if (params.store) {
      requestParams.store = params.store;
    }

    if (params.categoryId) {
      requestParams.categoryId = params.categoryId;
    }

    const token = localStorage.getItem('access_token');
    const response = await API.get<{ data: TrendingItemsData }>('/analytics/trending-items', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: requestParams,
    });

    if (response.data.data) {
      return response.data.data;
    } else {
      return rejectWithValue('Failed to fetch trending items data');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch trending items data'
    );
  }
});

const trendingItemsSlice = createSlice({
  name: 'trendingItems',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearTrendingItems: (state) => {
      state.data = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrendingItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrendingItems.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchTrendingItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = null;
      });
  },
});

export const { clearError, clearTrendingItems } = trendingItemsSlice.actions;
export default trendingItemsSlice.reducer;
